import React, { FC } from "react";

// Single source of truth for entity status/priority colors across in-project
// screens — consolidates dashboard.tsx's hex dict, GridView's Tag presets and
// the copy-pasted getPriorityColor helpers. See docs/pms-design-system.md §6.
//
// Status option lists are SERVER-DRIVEN (fetchStatusFiltersByModule) — lookup
// is normalized (lowercase, alphanumerics only) with a muted fallback; never
// an exhaustive switch.

export interface StatusTone {
  dot: string;
  text: string;
  bg: string;
}

const GOLD: StatusTone = { dot: "#faad14", text: "#ad6800", bg: "#fffbe6" };
const BLUE: StatusTone = { dot: "#1890ff", text: "#0958d9", bg: "#e6f4ff" };
const GREEN: StatusTone = { dot: "#52c41a", text: "#237804", bg: "#f6ffed" };
const DEEP_GREEN: StatusTone = { dot: "#389e0d", text: "#237804", bg: "#f6ffed" };
const CYAN: StatusTone = { dot: "#13c2c2", text: "#08979c", bg: "#e6fffb" };
const ORANGE: StatusTone = { dot: "#fa8c16", text: "#ad4e00", bg: "#fff7e6" };
const GREY: StatusTone = { dot: "#595959", text: "#434343", bg: "#f0f0f0" };
const RED: StatusTone = { dot: "#f5222d", text: "#a8071a", bg: "#fff1f0" };
const PURPLE: StatusTone = { dot: "#722ed1", text: "#531dab", bg: "#f9f0ff" };
const MUTED: StatusTone = { dot: "#8094ae", text: "#526484", bg: "#f0f2f5" };

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

const TONES: Record<string, StatusTone> = {
  yettostart: GOLD,
  open: BLUE,
  inprogress: GREEN,
  ontrack: GREEN,
  active: GREEN,
  underqc: CYAN,
  underqualitycheck: CYAN,
  underqualitychecks: CYAN,
  onhold: ORANGE,
  completed: DEEP_GREEN,
  approved: DEEP_GREEN,
  resolved: DEEP_GREEN,
  closed: GREY,
  deviated: RED,
  delayed: RED,
  overdue: RED,
  overduetasks: RED,
  rejected: RED,
  approvalrejected: RED,
  possibledelay: PURPLE,
  pendingapproval: GOLD,
  approvalpending: GOLD,
  draft: GOLD,
  inactive: MUTED,
  archived: MUTED,
  deleted: MUTED,
  // Procurement (PR/RFQ/PO) statuses
  awarded: DEEP_GREEN,
  delivered: DEEP_GREEN,
  negotiated: CYAN,
  partialconvert: ORANGE,
  partiallyconverted: ORANGE,
};

export const statusTone = (status?: string | null): StatusTone =>
  (status && TONES[norm(status)]) || MUTED;

export const priorityTone = (priority?: string | null): StatusTone => {
  switch (norm(priority || "")) {
    case "critical":
    case "urgent":
    case "high":
      return RED;
    case "medium":
      return ORANGE;
    case "low":
      return GREEN;
    default:
      return MUTED;
  }
};

// Hierarchy legend tokens (Tasks grid): Phase / Activity / Task
export const HIERARCHY_TONES = {
  phase: "#022c57",
  activity: "#2557d6",
  task: "#8094ae",
};

// Dot + tinted pill — same construction as the hub's project StatusPill.
export const GenericStatusPill: FC<{
  status?: string | null;
  label?: string;
  tone?: StatusTone;
}> = ({ status, label, tone }) => {
  const t = tone || statusTone(status);
  return (
    <span
      className="pms-status-pill"
      style={{ background: t.bg, color: t.text }}
    >
      <span className="pms-status-dot" style={{ background: t.dot }} />
      {label ?? status ?? "—"}
    </span>
  );
};

export const PriorityPill: FC<{ priority?: string | null }> = ({ priority }) => (
  <GenericStatusPill label={priority ?? "—"} tone={priorityTone(priority)} />
);

// Project-level statuses stay canonical in the hub
export {
  STATUS_META as PROJECT_STATUS_META,
  StatusPill as ProjectStatusPill,
  statusMetaFor,
} from "../projects/hub/statusMeta";
